import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import logoEdeli from '../assets/img/edeli_store.png';
import { successAlert, errorAlert } from '../utils/Alerts';

const RegisterClient = ({ theme, toggleTheme }) => {
  const [formData, setFormData] = useState({
    nombre: '', apellido: '', document: '', username: '', email: '', password: '', role: 'Usuario'
  });
  const [phone, setPhone] = useState('');
  const [countryISO, setCountryISO] = useState('VE');
  const [processing, setProcessing] = useState(false);
  const [step, setStep]   = useState(1); 
  const [code, setCode]   = useState('');
  // eslint-disable-next-line
  const [savedData, setSavedData] = useState(null); 
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  // const [showRoleOptions, setShowRoleOptions] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  
  // Estado para controlar qué campos han sido tocados
  const [touched, setTouched] = useState({});

  const navigate = useNavigate();
const location = useLocation();
const redirect = new URLSearchParams(location.search).get('redirect');
const loginUrl = redirect ? `/login?redirect=${redirect}` : '/login';
const isDarkMode = theme === 'dark';

const creatorSlug = redirect?.startsWith('/creador/') ? redirect.split('/creador/')[1] : null;
const [storeLogo, setStoreLogo] = useState(null);

useEffect(() => {
  if (!creatorSlug) return;
  axios.get(`${process.env.REACT_APP_API_URL}/api/user/store/${creatorSlug}`)
    .then(res => {
      setStoreLogo(res.data.store.logo);
      document.title = res.data.store.name || 'Edeli Store';

      if (res.data.store.logo) {
        let favicon = document.querySelector("link[rel~='icon']");
        if (!favicon) {
          favicon = document.createElement('link');
          favicon.rel = 'icon';
          document.head.appendChild(favicon);
        }
        favicon.href = res.data.store.logo;
      }
    })
    .catch(() => {});
}, [creatorSlug]);

const displayLogo = storeLogo || logoEdeli;

  // Validaciones dinámicas basadas en los campos REQUERIDOS del Schema
  const isPhoneInvalid = phone ? !isValidPhoneNumber(phone) : false;
  const isPhoneError = touched.celular && (!phone || isPhoneInvalid);
  const isUsernameError = touched.username && !formData.username;
  const isEmailError = touched.email && !formData.email;
  const isPasswordError = touched.password && !formData.password;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBlur = (e) => {
    setTouched({ ...touched, [e.target.name]: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

      setSubmitAttempted(true);
      setTouched({
        celular: true,
        username: true,
        email: true,
        password: true,
      });

      // Si hay errores, no continúa
      if (!phone || isPhoneInvalid || !formData.username || !formData.email || !formData.password) {
        return;
      }

      if (!acceptedTerms) {
        return errorAlert({ title: 'Términos', text: 'Debes aceptar los términos y condiciones' });
      }

    if (!phone || isPhoneInvalid) {
      return errorAlert({ 
        title: 'Teléfono inválido', 
        text: 'Por favor, introduce un número de teléfono real para continuar.' 
      });
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/user/register`, {
        ...formData, celular: phone, countryISO
      });
      if (response.status === 201) {
        successAlert({ title: '¡Registro Exitoso!', text: 'Tu cuenta ha sido creada.', timer: 3000 })
          .then(() => navigate(loginUrl));
      }
    } catch (error) {
      errorAlert({ title: 'Error', text: error.response?.data?.message || 'Error al registrar' });
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifyCode = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/user/register`, {
        ...savedData, code
      });
      if (response.status === 201) {
        successAlert({ title: '¡Registro Exitoso!', text: 'Tu cuenta ha sido creada.', timer: 3000 })
          .then(() => navigate(loginUrl));
      }
    } catch (error) {
      errorAlert({ title: 'Error', text: error.response?.data?.message || 'Código incorrecto' });
    } finally {
      setProcessing(false);
    }
  };

  return (
  <RegisterContainer>
    <RegisterCard isDarkMode={isDarkMode}>

      <BackButton onClick={() => navigate(loginUrl)} isDarkMode={isDarkMode}>←</BackButton>

      <SwitchContainer onClick={toggleTheme}>
        <SwitchLabel isDarkMode={isDarkMode}>{isDarkMode ? 'Oscuro' : 'Claro'}</SwitchLabel>
        <ToggleSlot active={isDarkMode}>
          <ToggleCircle active={isDarkMode}>{isDarkMode ? '🌙' : '☀️'}</ToggleCircle>
        </ToggleSlot>
      </SwitchContainer>

      <LogoImage src={displayLogo} alt="Logo" />
      <Title isDarkMode={isDarkMode}>Crear Cuenta</Title>

      <Form onSubmit={handleSubmit}>
        <FieldRow>
          <FieldGroup>
            <FieldLabel isDarkMode={isDarkMode}>Nombre</FieldLabel>
            <Input 
                isDarkMode={isDarkMode} 
                type="text" 
                name="nombre" 
                placeholder="Tu nombre" 
                onChange={handleChange} 
            />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel isDarkMode={isDarkMode}>Apellido</FieldLabel>
            <Input 
                isDarkMode={isDarkMode} 
                type="text" 
                name="apellido" 
                placeholder="Tu apellido" 
                onChange={handleChange} 
            />
          </FieldGroup>
        </FieldRow>

        <FieldRow>
          <FieldGroup>
            <FieldLabel isDarkMode={isDarkMode}>Teléfono <span style={{ color: '#ff4d4d' }}>*</span></FieldLabel>
            <PhoneInputWrapper 
                isDarkMode={isDarkMode} 
                hasError={isPhoneError}
                onBlur={() => setTouched({...touched, celular: true})}
            >
              <PhoneInput
                international
                defaultCountry="VE"
                value={phone}
                onChange={setPhone}
                onCountryChange={setCountryISO}
                placeholder="Número de teléfono"
              />
            </PhoneInputWrapper>
            {isPhoneError && (
              <ErrorMessage>{isPhoneInvalid ? 'Número no válido' : 'Campo obligatorio'}</ErrorMessage>
            )}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel isDarkMode={isDarkMode}>Usuario <span style={{ color: '#ff4d4d' }}>*</span></FieldLabel>
            <Input 
                isDarkMode={isDarkMode} 
                type="text" 
                name="username" 
                placeholder="Nombre de usuario" 
                onChange={handleChange} 
                onBlur={handleBlur}
                hasError={isUsernameError}
                required 
            />
            {isUsernameError && <ErrorMessage>Campo obligatorio</ErrorMessage>}
          </FieldGroup>
        </FieldRow>

        <FieldGroup>
          <FieldLabel isDarkMode={isDarkMode}>Email <span style={{ color: '#ff4d4d' }}>*</span></FieldLabel>
          <Input 
            isDarkMode={isDarkMode} 
            type="email" 
            name="email" 
            placeholder="correo@ejemplo.com" 
            onChange={handleChange} 
            onBlur={handleBlur}
            hasError={isEmailError}
            required 
          />
          {isEmailError && <ErrorMessage>Campo obligatorio</ErrorMessage>}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel isDarkMode={isDarkMode}>Contraseña <span style={{ color: '#ff4d4d' }}>*</span></FieldLabel>
          <Input 
            isDarkMode={isDarkMode} 
            type="password" 
            name="password" 
            placeholder="••••••••" 
            onChange={handleChange} 
            onBlur={handleBlur}
            hasError={isPasswordError}
            required 
          />
          {isPasswordError && <ErrorMessage>Campo obligatorio</ErrorMessage>}
        </FieldGroup>

        {/* <FieldGroup style={{ position: 'relative' }}>
          <FieldLabel isDarkMode={isDarkMode}>Rol</FieldLabel>
          <RoleSelector isDarkMode={isDarkMode} onClick={() => setShowRoleOptions(!showRoleOptions)}>
            {formData.role === 'Usuario' ? 'Usuario (Comercializador...)' : 
            formData.role === 'Creador' ? 'Creador (Comercializador + Subir...)' : 
            'Seleccione un rol'}
            <span>▾</span>
          </RoleSelector>
          {showRoleOptions && (
            <RoleOptions isDarkMode={isDarkMode}>
              <RoleOption isDarkMode={isDarkMode} onClick={() => { setFormData({...formData, role: 'Usuario'}); setShowRoleOptions(false); }}>
                Usuario (Comercializador...)
              </RoleOption>
              <RoleOption isDarkMode={isDarkMode} onClick={() => { setFormData({...formData, role: 'Creador'}); setShowRoleOptions(false); }}>
                Creador (Comercializador + Subir...)
              </RoleOption>
            </RoleOptions>
          )}
        </FieldGroup> */}

        <TermsRow>
          <TermsCheckbox
            type="checkbox"
            id="terms"
            checked={acceptedTerms}
            onChange={e => setAcceptedTerms(e.target.checked)}
          />
          <TermsLabel isDarkMode={isDarkMode} htmlFor="terms">
            Acepto los{' '}
            <TermsLink onClick={() => setShowTerms(true)}>
              Términos y Condiciones
            </TermsLink>
          </TermsLabel>
        </TermsRow>

        {submitAttempted && !acceptedTerms && (
          <ErrorMessage>Debes aceptar los términos y condiciones</ErrorMessage>
        )}

        <MainButton 
          isDarkMode={isDarkMode} 
          type="submit" 
          disabled={processing}
        >
          {processing ? 'Cargando...' : 'Registrar'}
        </MainButton>
      </Form>

    </RegisterCard>

    {/* ── MODAL VERIFICACIÓN ── */}
    {step === 2 && (
      <ModalOverlay>
        <ModalBox isDarkMode={isDarkMode}>

          {/* Ícono */}
          <ModalIconWrap>
            <ModalIcon>✉️</ModalIcon>
          </ModalIconWrap>

          <ModalTitle isDarkMode={isDarkMode}>Verifica tu correo</ModalTitle>
          <ModalText isDarkMode={isDarkMode}>
            Enviamos un código de 6 dígitos a
          </ModalText>
          <ModalEmail isDarkMode={isDarkMode}>{savedData?.email}</ModalEmail>

          <CodeInput
            isDarkMode={isDarkMode}
            type="text"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            autoFocus
          />

          <ModalExpire isDarkMode={isDarkMode}>⏱ El código expira en 10 minutos</ModalExpire>

          <ConfirmBtn
            onClick={handleVerifyCode}
            disabled={processing || code.length < 6}
          >
            {processing ? 'Verificando...' : 'Confirmar Código'}
          </ConfirmBtn>

          <ResendBtn isDarkMode={isDarkMode} onClick={handleSubmit} disabled={processing}>
            ¿No recibiste el código? Reenviar
          </ResendBtn>

          <BackToForm isDarkMode={isDarkMode} onClick={() => { setStep(1); setCode(''); }}>
            ← Volver al formulario
          </BackToForm>

        </ModalBox>
      </ModalOverlay>
    )}

    {showTerms && (
      <ModalOverlay onClick={() => setShowTerms(false)}>
        <TermsBox isDarkMode={isDarkMode} onClick={e => e.stopPropagation()}>
          <TermsHeader isDarkMode={isDarkMode}>
            <TermsTitle isDarkMode={isDarkMode}>Términos y Condiciones</TermsTitle>
            <CloseTermsBtn isDarkMode={isDarkMode} onClick={() => setShowTerms(false)}>✕</CloseTermsBtn>
          </TermsHeader>

          <TermsContent isDarkMode={isDarkMode}>
            <TermsSection isDarkMode={isDarkMode}>
              <TermsSectionTitle isDarkMode={isDarkMode}>1. Aceptación</TermsSectionTitle>
              <TermsP isDarkMode={isDarkMode}>Al registrarte en Edeli Store aceptas cumplir estos términos. Si no estás de acuerdo, no uses la plataforma.</TermsP>
            </TermsSection>

            <TermsSection isDarkMode={isDarkMode}>
              <TermsSectionTitle isDarkMode={isDarkMode}>2. Uso de la Plataforma</TermsSectionTitle>
              <TermsP isDarkMode={isDarkMode}>Edeli Store es una plataforma de comercio digital para compartir y vender contenido digital organizado por temáticas.</TermsP>
            </TermsSection>

            <TermsSection isDarkMode={isDarkMode}>
              <TermsSectionTitle isDarkMode={isDarkMode}>3. Contenido Prohibido</TermsSectionTitle>
              <TermsP isDarkMode={isDarkMode}>Está estrictamente prohibido subir o compartir:</TermsP>
              <TermsList isDarkMode={isDarkMode}>
                <li>Contenido sexual explícito, pornográfico u obsceno</li>
                <li>Material que involucre menores de edad de forma inapropiada</li>
                <li>Contenido que promueva violencia, odio, discriminación o terrorismo</li>
                <li>Material con derechos de autor sin autorización del titular</li>
                <li>Contenido ilícito, fraudulento o que viole leyes vigentes</li>
                <li>Spam, malware o cualquier contenido malicioso</li>
              </TermsList>
            </TermsSection>

            <TermsSection isDarkMode={isDarkMode}>
              <TermsSectionTitle isDarkMode={isDarkMode}>4. Responsabilidad del Contenido</TermsSectionTitle>
              <TermsP isDarkMode={isDarkMode}>Cada usuario es responsable del contenido que sube. Edeli Store se reserva el derecho de eliminar contenido que viole estas normas y suspender cuentas infractoras sin previo aviso.</TermsP>
            </TermsSection>

            <TermsSection isDarkMode={isDarkMode}>
              <TermsSectionTitle isDarkMode={isDarkMode}>5. Pagos y Transacciones</TermsSectionTitle>
              <TermsP isDarkMode={isDarkMode}>Los precios son establecidos por los creadores. Edeli Store actúa como intermediario. Las condiciones específicas de pago serán detalladas próximamente.</TermsP>
            </TermsSection>

            <TermsSection isDarkMode={isDarkMode}>
              <TermsSectionTitle isDarkMode={isDarkMode}>6. Privacidad</TermsSectionTitle>
              <TermsP isDarkMode={isDarkMode}>Tu información personal es tratada conforme a nuestra Política de Privacidad. No vendemos ni compartimos tus datos con terceros sin tu consentimiento.</TermsP>
            </TermsSection>

            <TermsSection isDarkMode={isDarkMode}>
              <TermsSectionTitle isDarkMode={isDarkMode}>7. Modificaciones</TermsSectionTitle>
              <TermsP isDarkMode={isDarkMode}>Edeli Store puede modificar estos términos en cualquier momento. Los usuarios serán notificados de cambios importantes.</TermsP>
            </TermsSection>

            <TermsSection isDarkMode={isDarkMode}>
              <TermsSectionTitle isDarkMode={isDarkMode}>8. Contacto</TermsSectionTitle>
              <TermsP isDarkMode={isDarkMode}>Para consultas: jesus.e.elias.s@gmail.com</TermsP>
            </TermsSection>
          </TermsContent>

          <TermsFooter isDarkMode={isDarkMode}>
            <AcceptTermsBtn onClick={() => { setAcceptedTerms(true); setShowTerms(false); }}>
              ✓ Aceptar y cerrar
            </AcceptTermsBtn>
          </TermsFooter>
        </TermsBox>
      </ModalOverlay>
    )}

  </RegisterContainer>
  
);
};

/* ═══════════════════════════════════════════
   STYLED COMPONENTS
═══════════════════════════════════════════ */

const RegisterContainer = styled.div`
  display: flex;
  align-items: center; /* ← volver a center */
  justify-content: center;
  width: 100vw;
  min-height: 100vh;
  background-color: var(--bg-app);
  padding: 30px 10px;
  box-sizing: border-box;
`;

const RegisterCard = styled.div`
  position: relative;
  background-color: ${p => p.isDarkMode ? '#2c2c2c' : '#fcfcfc'};
  padding: 50px 24px 32px; /* ← reducido padding lateral en móvil */
  border-radius: 14px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
  border: 1px solid ${p => p.isDarkMode ? '#3d3d3d' : '#eeeeee'};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 480px;
  overflow: visible; /* ← permite que el dropdown se vea */
`;

const BackButton = styled.button`
  position: absolute; top: 15px; left: 15px;
  background: none; border: none; font-size: 22px;
  color: ${p => p.isDarkMode ? '#fff' : '#333'};
  cursor: pointer;
  &:hover { transform: translateX(-3px); }
`;

const SwitchContainer = styled.div`
  position: absolute; top: 15px; right: 15px;
  display: flex; align-items: center; gap: 8px; cursor: pointer;
`;

const SwitchLabel = styled.span`
  font-size: 10px; font-weight: bold;
  color: ${p => p.isDarkMode ? '#bbb' : '#666'};
`;

const ToggleSlot = styled.div`
  width: 34px; height: 18px;
  background-color: ${p => p.active ? '#555' : '#ccc'};
  border-radius: 20px; position: relative;
`;

const ToggleCircle = styled.div`
  position: absolute; top: 2px;
  left: ${p => p.active ? '18px' : '2px'};
  width: 14px; height: 14px;
  background-color: white; border-radius: 50%;
  transition: all 0.3s ease; font-size: 8px;
  display: flex; align-items: center; justify-content: center;
`;

const LogoImage = styled.img`
  width: 90px; height: auto; margin-bottom: 6px;
`;

const Title = styled.h2`
  color: ${p => p.isDarkMode ? '#fff' : '#333'};
  margin-bottom: 20px; font-size: 20px; font-weight: 700;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px; /* ← reducido gap */
  overflow: visible; /* ← permite que el dropdown se vea */
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  @media (max-width: 420px) { grid-template-columns: 1fr; } /* ← más amplio el breakpoint */
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${p => p.isDarkMode ? '#888' : '#999'};
`;

const Input = styled.input`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${p => p.isDarkMode ? '#444' : '#ddd'};
  background-color: ${p => p.isDarkMode ? '#3d3d3d' : '#f7f8fa'};
  color: ${p => p.isDarkMode ? '#e0e0e0' : '#333'};
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: rgba(79,70,229,0.6); }
  &::placeholder { color: ${p => p.isDarkMode ? '#666' : '#bbb'}; }
`;

// const RoleSelector = styled.div`
//   padding: 10px 12px;
//   border-radius: 8px;
//   border: 1px solid ${p => p.isDarkMode ? '#444' : '#ddd'};
//   background-color: ${p => p.isDarkMode ? '#3d3d3d' : '#f7f8fa'};
//   color: ${p => p.isDarkMode ? '#e0e0e0' : '#333'};
//   font-size: 14px;
//   cursor: pointer;
//   display: flex;
//   justify-content: space-between;
//   align-items: center;
//   user-select: none;
// `;

// const RoleOptions = styled.div`
//   position: absolute;
//   top: 100%;
//   left: 0; right: 0;
//   background: ${p => p.isDarkMode ? '#2c2c2c' : '#fff'};
//   border: 1px solid ${p => p.isDarkMode ? '#444' : '#ddd'};
//   border-radius: 8px;
//   z-index: 100;
//   margin-top: 4px;
//   box-shadow: 0 4px 12px rgba(0,0,0,0.2);
// `;

// const RoleOption = styled.div`
//   padding: 10px 12px;
//   color: ${p => p.isDarkMode ? '#e0e0e0' : '#333'};
//   font-size: 14px;
//   cursor: pointer;
//   &:hover { background: ${p => p.isDarkMode ? '#444' : '#f0f0f0'}; }
//   &:first-child { border-radius: 8px 8px 0 0; }
//   &:last-child { border-radius: 0 0 8px 8px; }
// `;

const PhoneInputWrapper = styled.div`
  .PhoneInput {
    background: ${p => p.isDarkMode ? '#3d3d3d' : '#f7f8fa'};
    border: 1px solid ${p => p.isDarkMode ? '#444' : '#ddd'};
    border-radius: 8px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: border-color 0.2s;
    &:focus-within { border-color: rgba(79,70,229,0.6); }
  }
  .PhoneInputInput {
    background: transparent;
    border: none; outline: none;
    color: ${p => p.isDarkMode ? '#e0e0e0' : '#333'};
    font-size: 14px; width: 100%;
    &::placeholder { color: ${p => p.isDarkMode ? '#666' : '#bbb'}; }
  }
  .PhoneInputCountrySelect {
    background: ${p => p.isDarkMode ? '#3d3d3d' : '#f7f8fa'};
    color: ${p => p.isDarkMode ? '#e0e0e0' : '#333'};
    border: none; outline: none; cursor: pointer;
  }
  .PhoneInputCountryIcon {
    width: 22px; height: 16px;
  }
`;

const ErrorMessage = styled.span`
  color: #ff4d4d;
  font-size: 10px;
  font-weight: 600;
  margin-top: -2px;
  margin-left: 2px;
`;

const MainButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: white; padding: 12px;
  border: none; border-radius: 8px;
  font-weight: 700; font-size: 15px;
  cursor: pointer; margin-top: 4px;
  transition: all 0.2s;
  &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CodeInput = styled.input`
  width: 180px;
  text-align: center;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 8px;
  padding: 12px;
  border-radius: 10px;
  border: 2px solid ${p => p.isDarkMode ? '#444' : '#ddd'};
  background: ${p => p.isDarkMode ? '#3d3d3d' : '#f7f8fa'};
  color: ${p => p.isDarkMode ? '#fff' : '#333'};
  outline: none;
  &:focus { border-color: #4f46e5; }
`;

const ResendBtn = styled.button`
  background: none; border: none;
  color: #4f46e5; font-size: 13px;
  cursor: pointer; text-decoration: underline;
  &:hover { opacity: 0.7; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const ModalOverlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease;
  @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
`;

const ModalBox = styled.div`
  background: ${p => p.isDarkMode ? '#1e1e1e' : '#fff'};
  border: 1px solid ${p => p.isDarkMode ? '#2a2a2a' : '#e8e8e8'};
  border-radius: 20px;
  padding: 40px 36px;
  width: 100%; max-width: 420px;
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.4);
  animation: slideUp 0.25s ease;
  @keyframes slideUp {
    from { transform: translateY(24px); opacity: 0 }
    to   { transform: translateY(0);    opacity: 1 }
  }
  @media (max-width: 480px) { margin: 0 16px; padding: 32px 20px; }
`;

const ModalIconWrap = styled.div`
  width: 70px; height: 70px; border-radius: 50%;
  background: rgba(79,70,229,0.1);
  border: 2px solid rgba(79,70,229,0.3);
  display: flex; align-items: center; justify-content: center;
  font-size: 30px;
  margin-bottom: 4px;
`;

const ModalIcon = styled.span`font-size: 32px;`;

const ModalTitle = styled.h3`
  margin: 0; font-size: 20px; font-weight: 800;
  color: ${p => p.isDarkMode ? '#fff' : '#111'};
`;

const ModalText = styled.p`
  margin: 0; font-size: 14px; text-align: center;
  color: ${p => p.isDarkMode ? '#888' : '#666'};
`;

const ModalEmail = styled.span`
  font-size: 14px; font-weight: 700;
  color: #4f46e5;
`;

const ModalExpire = styled.p`
  margin: 0; font-size: 12px;
  color: ${p => p.isDarkMode ? '#555' : '#bbb'};
`;

const ConfirmBtn = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: #fff; padding: 13px;
  border: none; border-radius: 10px;
  font-weight: 700; font-size: 15px; cursor: pointer;
  margin-top: 4px; transition: all 0.2s;
  &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const BackToForm = styled.button`
  background: none; border: none;
  font-size: 12px; cursor: pointer;
  color: ${p => p.isDarkMode ? '#555' : '#bbb'};
  &:hover { color: ${p => p.isDarkMode ? '#888' : '#666'}; }
`;

const TermsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TermsCheckbox = styled.input`
  width: 16px; height: 16px;
  accent-color: #4f46e5;
  cursor: pointer;
  flex-shrink: 0;
`;

const TermsLabel = styled.label`
  font-size: 13px;
  color: ${p => p.isDarkMode ? '#888' : '#666'};
  cursor: pointer;
`;

const TermsLink = styled.span`
  color: #4f46e5;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  &:hover { opacity: 0.7; }
`;

const TermsBox = styled.div`
  background: ${p => p.isDarkMode ? '#1e1e1e' : '#fff'};
  border: 1px solid ${p => p.isDarkMode ? '#2a2a2a' : '#e8e8e8'};
  border-radius: 16px;
  width: 100%; max-width: 560px;
  max-height: 80vh;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,0.4);
  animation: slideUp 0.25s ease;
  @media (max-width: 580px) { margin: 0 12px; }
`;

const TermsHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid ${p => p.isDarkMode ? '#2a2a2a' : '#f0f0f0'};
  flex-shrink: 0;
`;

const TermsTitle = styled.h3`
  margin: 0; font-size: 16px; font-weight: 700;
  color: ${p => p.isDarkMode ? '#fff' : '#111'};
`;

const CloseTermsBtn = styled.button`
  background: ${p => p.isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  border: 1px solid ${p => p.isDarkMode ? '#2a2a2a' : '#eee'};
  color: ${p => p.isDarkMode ? '#888' : '#999'};
  width: 30px; height: 30px; border-radius: 8px;
  cursor: pointer; font-size: 13px;
  display: flex; align-items: center; justify-content: center;
  &:hover { color: #dc3545; border-color: rgba(220,53,69,0.4); }
`;

const TermsContent = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
  display: flex; flex-direction: column; gap: 18px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${p => p.isDarkMode ? '#333' : '#ddd'}; border-radius: 4px; }
`;

const TermsSection = styled.div`display: flex; flex-direction: column; gap: 6px;`;

const TermsSectionTitle = styled.h4`
  margin: 0; font-size: 13px; font-weight: 700;
  color: #4f46e5;
  text-transform: uppercase; letter-spacing: 0.5px;
`;

const TermsP = styled.p`
  margin: 0; font-size: 13px; line-height: 1.6;
  color: ${p => p.isDarkMode ? '#888' : '#666'};
`;

const TermsList = styled.ul`
  margin: 6px 0 0 16px; padding: 0;
  display: flex; flex-direction: column; gap: 4px;
  font-size: 13px; line-height: 1.6;
  color: ${p => p.isDarkMode ? '#888' : '#666'};
`;

const TermsFooter = styled.div`
  padding: 16px 24px 20px;
  border-top: 1px solid ${p => p.isDarkMode ? '#2a2a2a' : '#f0f0f0'};
  flex-shrink: 0;
`;

const AcceptTermsBtn = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: #fff; padding: 12px;
  border: none; border-radius: 10px;
  font-weight: 700; font-size: 14px; cursor: pointer;
  transition: all 0.2s;
  &:hover { opacity: 0.88; transform: translateY(-1px); }
`;

export default RegisterClient;